/**
 * Which token goes on the wire.
 *
 * cloud-core and cloud-intersection-broker validate the Cognito **ID token** —
 * it is the only token that carries `cognito:groups` and `custom:company_ids`,
 * and their `AUTH_OIDC_TOKEN_USE` is pinned to `id`. So every cloud-core call
 * (HTTP and websocket `login`) and every broker call sends
 * `Bearer <rawIdToken>`. An access token presented to the RBAC broker is
 * routed to its M2M client-credentials path and rejected.
 *
 * The access token stays reachable through `getRawAccessToken()` for
 * Cognito-managed-login calls only. All tokens come from the shared
 * `@cyngn/auth-react` store (`cyngn_auth.*`); there is no legacy bearer.
 */
import { decodeClaims } from "@cyngn/auth-react";

import { appTokenStore } from "./authTokenStore";

const BEARER_PREFIX = "Bearer ";

export const getRawIdToken = (): string | null => appTokenStore.read().idToken;

/**
 * The raw Cognito access token of the current session, or null when there is
 * no session. Only Cognito-managed-login calls may use it.
 */
export const getRawAccessToken = (): string | null =>
  appTokenStore.read().accessToken;

/**
 * The `Authorization` header value for cloud-core and the intersection broker:
 * the ID token of the shared session, or null when there is none.
 */
export const getApiBearerToken = (): string | null => {
  const idToken = appTokenStore.bearerToken();
  return idToken ? `${BEARER_PREFIX}${idToken}` : null;
};

/**
 * When the session's bearer stops being accepted, as a millisecond timestamp.
 *
 * The refresh clock has to follow the token that is actually presented: with
 * `AUTH_OIDC_TOKEN_USE=id` cloud-core validates the ID token's `exp`, so a
 * session whose stored expiry came from the access token's `expiresIn` would
 * keep sending an ID token the backend has already rejected whenever the two
 * lifetimes differ. `expiresInSeconds` is the fallback for a token whose
 * claims cannot be read.
 *
 * An `exp` that is already past is ignored: that happens when a refresh
 * response carries no new ID token and the retained one has run out, and
 * storing a past expiry would make the refresh timer fire in a loop. The
 * `expiresInSeconds` window keeps that case behaving as it did before.
 */
export const bearerExpiresAtMs = (
  idToken: string | null | undefined,
  expiresInSeconds: number | null | undefined
): number => {
  const exp = decodeClaims(idToken)?.exp;
  if (exp && exp * 1000 > Date.now()) {
    return exp * 1000;
  }
  return Date.now() + (expiresInSeconds ?? 0) * 1000;
};
